import React, { useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { calculateFinMapleScore } from '../../utils/finmapleScore';
import { useTranslation } from 'react-i18next';
import { getCardRewards } from '../../data/cardTranslations';
import CardImage from './CardImage';
import { parseFee, parseRate } from './helpers';

/* ── i18n labels ── */
const UI_LABELS = {
  es: {
    title: 'Comparación de tarjetas',
    subtitle: (n) => `${n} tarjetas · Encuentra la mejor para ti`,
    close: 'Cerrar comparación',
    feature: 'Característica',
    remove: 'Quitar',
    apply: 'Solicitar',
    best: 'Mejor',
    noFee: 'Sin cuota',
    noRewards: 'Sin recompensas',
    notRequired: 'No requerido',
    included: 'Incluido',
    yes: 'Sí',
    no: 'No',
    sections: { costs: 'Costos y tasas', profile: 'Perfil y beneficios', insurance: 'Seguros incluidos' },
    rows: {
      annual_fee: 'Cuota anual', purchase_rate: 'Tasa de compra', cash_advance_rate: 'Tasa de adelanto',
      balance_transfer_rate: 'Tasa de transferencia', foreign_conversion_fee: 'Cargo moneda extranjera',
      newcomer_friendly: 'Newcomer friendly', rewards: 'Recompensas', min_income_personal: 'Ingreso mínimo',
      mobile_device: 'Seguro dispositivos', travel_medical: 'Seguro viaje médico',
      rental_vehicle: 'Seguro vehículo rental', purchase_protection: 'Purchase protection',
    },
    tooltips: {
      annual_fee: 'El costo anual que pagas por tener esta tarjeta.',
      purchase_rate: 'Porcentaje de interés sobre compras no pagadas. Menor es mejor.',
      cash_advance_rate: 'Interés que se cobra al retirar efectivo con la tarjeta.',
      balance_transfer_rate: 'Interés al transferir saldo de otra tarjeta.',
      foreign_conversion_fee: 'Porcentaje que te cobran al comprar en moneda extranjera.',
      newcomer_friendly: 'Diseñada para personas sin historial crediticio canadiense.',
      rewards: 'Beneficios al usar la tarjeta: cash back, puntos, millas.',
      min_income_personal: 'Ingreso personal mínimo requerido para aplicar.',
      mobile_device: 'Protección para tu celular contra daños o robo.',
      travel_medical: 'Cobertura de gastos médicos al viajar fuera de Canadá.',
      rental_vehicle: 'Cobertura al rentar un auto y pagar con esta tarjeta.',
      purchase_protection: 'Protección de compras contra daños o robo.',
    },
  },
  en: {
    title: 'Card comparison',
    subtitle: (n) => `${n} cards · Find the best one for you`,
    close: 'Close comparison',
    feature: 'Feature',
    remove: 'Remove',
    apply: 'Apply',
    best: 'Best',
    noFee: 'No fee',
    noRewards: 'No rewards',
    notRequired: 'Not required',
    included: 'Included',
    yes: 'Yes',
    no: 'No',
    sections: { costs: 'Costs & rates', profile: 'Profile & benefits', insurance: 'Included insurance' },
    rows: {
      annual_fee: 'Annual fee', purchase_rate: 'Purchase rate', cash_advance_rate: 'Cash advance rate',
      balance_transfer_rate: 'Balance transfer rate', foreign_conversion_fee: 'Foreign transaction fee',
      newcomer_friendly: 'Newcomer friendly', rewards: 'Rewards', min_income_personal: 'Minimum income',
      mobile_device: 'Device insurance', travel_medical: 'Travel medical insurance',
      rental_vehicle: 'Rental vehicle insurance', purchase_protection: 'Purchase protection',
    },
    tooltips: {
      annual_fee: 'The annual cost of holding this card.',
      purchase_rate: 'Interest rate on unpaid purchases. Lower is better.',
      cash_advance_rate: 'Interest charged when withdrawing cash with the card.',
      balance_transfer_rate: 'Interest when transferring a balance from another card.',
      foreign_conversion_fee: 'Fee charged for purchases in a foreign currency.',
      newcomer_friendly: 'Designed for people without Canadian credit history.',
      rewards: 'Benefits from using the card: cash back, points, miles.',
      min_income_personal: 'Minimum personal income required to apply.',
      mobile_device: 'Protection for your phone against damage or theft.',
      travel_medical: 'Medical expense coverage when travelling outside Canada.',
      rental_vehicle: 'Coverage when renting a car and paying with this card.',
      purchase_protection: 'Protection for purchases against damage or theft.',
    },
  },
  fr: {
    title: 'Comparaison de cartes',
    subtitle: (n) => `${n} cartes · Trouvez la meilleure pour vous`,
    close: 'Fermer la comparaison',
    feature: 'Caractéristique',
    remove: 'Retirer',
    apply: 'Demander',
    best: 'Meilleure',
    noFee: 'Sans frais',
    noRewards: 'Pas de récompenses',
    notRequired: 'Non requis',
    included: 'Inclus',
    yes: 'Oui',
    no: 'Non',
    sections: { costs: 'Coûts et taux', profile: 'Profil et avantages', insurance: 'Assurances incluses' },
    rows: {
      annual_fee: 'Frais annuels', purchase_rate: "Taux d'achat", cash_advance_rate: "Taux d'avance de fonds",
      balance_transfer_rate: 'Taux de transfert', foreign_conversion_fee: 'Frais de change',
      newcomer_friendly: 'Adapté aux nouveaux arrivants', rewards: 'Récompenses', min_income_personal: 'Revenu minimum',
      mobile_device: 'Assurance appareil', travel_medical: 'Assurance voyage médicale',
      rental_vehicle: 'Assurance véhicule de location', purchase_protection: 'Protection des achats',
    },
    tooltips: {
      annual_fee: 'Le coût annuel pour détenir cette carte.',
      purchase_rate: "Taux d'intérêt sur les achats impayés. Plus bas est mieux.",
      cash_advance_rate: "Intérêt facturé pour retirer de l'argent avec la carte.",
      balance_transfer_rate: "Intérêt lors du transfert d'un solde d'une autre carte.",
      foreign_conversion_fee: 'Frais facturés pour les achats en devise étrangère.',
      newcomer_friendly: 'Conçue pour les personnes sans historique de crédit canadien.',
      rewards: 'Avantages en utilisant la carte : remise en argent, points, miles.',
      min_income_personal: 'Revenu personnel minimum requis pour faire une demande.',
      mobile_device: 'Protection de votre téléphone contre les dommages ou le vol.',
      travel_medical: 'Couverture médicale lors de voyages hors du Canada.',
      rental_vehicle: 'Couverture lors de la location d\'un véhicule payé avec cette carte.',
      purchase_protection: 'Protection des achats contre les dommages ou le vol.',
    },
  },
};

/* ── Helpers ─────────────────────────────────────── */

const scoreTheme = (total) => {
  if (total >= 4.5) return { color: '#2E7D32', ring: '#2E7D32' };
  if (total >= 3.5) return { color: '#1E3A5F', ring: '#1E3A5F' };
  return { color: '#F9A825', ring: '#F9A825' };
};

const pillarColor = (val) => {
  if (val >= 4.0) return '#2E7D32';
  if (val >= 3.0) return '#F9A825';
  return '#C62828';
};

const stickyLabelSx = (bgcolor = '#fff') => ({
  fontWeight: 600,
  color: '#1A1A1A',
  minWidth: { xs: 90, sm: 160 },
  maxWidth: { xs: 110, sm: 'none' },
  position: 'sticky',
  left: 0,
  bgcolor,
  zIndex: 1,
  borderRight: '1px solid #E6E9EE',
  fontSize: { xs: '0.75rem', sm: '0.875rem' },
  px: { xs: 1, sm: 2 },
});

const insuranceValue = (val) => {
  if (!val || val === 'Not available') return null;
  return val;
};

/* ── Score pillars ── */

const PILLARS = [
  { key: 'costos', emoji: '💰' },
  { key: 'newcomer', emoji: '🍁' },
  { key: 'digital', emoji: '📱' },
  { key: 'beneficios', emoji: '🎁' },
  { key: 'espanol', emoji: '🗣️' },
];

/* ── CompactScore sub-component ── */

const CompactScore = ({ card, isWinner }) => {
  const { total, breakdown } = calculateFinMapleScore(card);
  const st = scoreTheme(total);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75, py: 1 }}>
      <Box
        sx={{
          width: 52,
          height: 52,
          borderRadius: '50%',
          border: `3px solid ${st.ring}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', color: st.color }}>
          {total}
        </Typography>
      </Box>

      {isWinner && (
        <Chip
          icon={<EmojiEventsIcon sx={{ fontSize: 16 }} />}
          label={l.best}
          size="small"
          sx={{
            bgcolor: '#FFF8E1',
            color: '#7D5A00',
            fontWeight: 600,
            fontSize: '0.75rem',
            height: 22,
            '& .MuiChip-icon': { color: '#F9A825' },
          }}
        />
      )}

      <Box sx={{ width: '100%', maxWidth: 130, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
        {PILLARS.map((p) => {
          const val = breakdown[p.key];
          return (
            <Box key={p.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography sx={{ fontSize: '0.75rem', width: 16, textAlign: 'center', lineHeight: 1 }}>
                {p.emoji}
              </Typography>
              <Box sx={{ flex: 1, height: 4, bgcolor: '#EEF0F3', borderRadius: 2, overflow: 'hidden' }}>
                <Box
                  sx={{
                    width: `${(val / 5) * 100}%`,
                    height: '100%',
                    bgcolor: pillarColor(val),
                    borderRadius: 2,
                    transition: 'width 0.4s ease',
                  }}
                />
              </Box>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#5F6B7A', width: 20, textAlign: 'right' }}>
                {val.toFixed(1)}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

/* ── Comparison rows (sectioned) — built per-language ── */

const buildRows = (l, lang) => {
  const checkMark = (label) => (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      <CheckCircleOutlineIcon sx={{ fontSize: 18, color: '#2E7D32' }} />
      <Typography component="span" sx={{ color: '#2E7D32', fontWeight: 500, fontSize: '0.875rem' }}>{label}</Typography>
    </Box>
  );
  const dash = <Typography component="span" sx={{ color: '#5F6B7A', fontSize: '0.875rem' }}>—</Typography>;

  const rateBestFn = (field) => (cards) => {
    const rates = cards.map((c) => ({ id: c.id, r: parseRate(c[field]) })).filter((x) => x.r !== null);
    if (rates.length === 0) return [];
    const min = Math.min(...rates.map((x) => x.r));
    return rates.filter((x) => x.r === min).map((x) => x.id);
  };

  return [
    { type: 'section', title: l.sections.costs, icon: '💰' },
    {
      type: 'data', key: 'annual_fee', label: l.rows.annual_fee, tooltip: l.tooltips.annual_fee,
      render: (card) => {
        const s = String(card.annual_fee || '').toLowerCase();
        if (s.includes('no annual fee') || s === '0')
          return <Typography component="span" sx={{ color: '#2E7D32', fontWeight: 600, fontSize: '0.875rem' }}>{l.noFee}</Typography>;
        const m = s.match(/([\d.]+)/);
        return m ? `$${parseFloat(m[1]).toFixed(0)} CAD` : card.annual_fee;
      },
      bestFn: (cards) => {
        const fees = cards.map((c) => parseFee(c.annual_fee));
        const min = Math.min(...fees);
        return cards.filter((c) => parseFee(c.annual_fee) === min).map((c) => c.id);
      },
    },
    { type: 'data', key: 'purchase_rate', label: l.rows.purchase_rate, tooltip: l.tooltips.purchase_rate, render: (card) => card.purchase_rate || 'N/D', bestFn: rateBestFn('purchase_rate') },
    { type: 'data', key: 'cash_advance_rate', label: l.rows.cash_advance_rate, tooltip: l.tooltips.cash_advance_rate, render: (card) => card.cash_advance_rate || 'N/D', bestFn: rateBestFn('cash_advance_rate') },
    { type: 'data', key: 'balance_transfer_rate', label: l.rows.balance_transfer_rate, tooltip: l.tooltips.balance_transfer_rate, render: (card) => card.balance_transfer_rate || 'N/D', bestFn: rateBestFn('balance_transfer_rate') },
    { type: 'data', key: 'foreign_conversion_fee', label: l.rows.foreign_conversion_fee, tooltip: l.tooltips.foreign_conversion_fee, render: (card) => card.foreign_conversion_fee || 'N/D', bestFn: rateBestFn('foreign_conversion_fee') },
    { type: 'section', title: l.sections.profile, icon: '🎁' },
    {
      type: 'data', key: 'newcomer_friendly', label: l.rows.newcomer_friendly, tooltip: l.tooltips.newcomer_friendly,
      render: (card) => card.newcomer_friendly ? checkMark(l.yes) : <Typography component="span" sx={{ color: '#5F6B7A', fontSize: '0.875rem' }}>{l.no}</Typography>,
    },
    {
      type: 'data', key: 'rewards', label: l.rows.rewards, tooltip: l.tooltips.rewards,
      render: (card) => {
        const r = getCardRewards(card.id, card.rewards || '', lang);
        if (!r || r.toLowerCase().includes('not available') || r.toLowerCase() === 'no rewards') return l.noRewards;
        return r.length > 80 ? r.slice(0, 77) + '...' : r;
      },
    },
    {
      type: 'data', key: 'min_income_personal', label: l.rows.min_income_personal, tooltip: l.tooltips.min_income_personal,
      render: (card) => {
        const v = card.min_income_personal;
        if (!v || v === 'N/A' || String(v).toLowerCase().includes('not required'))
          return <Typography component="span" sx={{ color: '#2E7D32', fontWeight: 500, fontSize: '0.875rem' }}>{l.notRequired}</Typography>;
        const m = String(v).match(/([\d,]+)/);
        return m ? `$${m[1]} CAD` : v;
      },
    },
    { type: 'section', title: l.sections.insurance, icon: '🛡️' },
    { type: 'data', key: 'mobile_device', label: l.rows.mobile_device, tooltip: l.tooltips.mobile_device, render: (card) => insuranceValue(card.insurance?.mobile_device) ? checkMark(l.included) : dash },
    { type: 'data', key: 'travel_medical', label: l.rows.travel_medical, tooltip: l.tooltips.travel_medical, render: (card) => insuranceValue(card.insurance?.travel_medical) ? checkMark(l.included) : dash },
    { type: 'data', key: 'rental_vehicle', label: l.rows.rental_vehicle, tooltip: l.tooltips.rental_vehicle, render: (card) => insuranceValue(card.insurance?.rental_vehicle) ? checkMark(l.included) : dash },
    {
      type: 'data', key: 'purchase_protection', label: l.rows.purchase_protection, tooltip: l.tooltips.purchase_protection,
      render: (card) => {
        const raw = card.insurance?.mobile_device || '';
        return raw.toLowerCase().includes('purchase protection') ? checkMark(l.included) : dash;
      },
    },
  ];
};

/* ── Main component ──────────────────────────────── */

const ComparisonModal = ({ open, onClose, cards, onRemoveCard }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { i18n } = useTranslation();
  const lang = i18n.language || 'es';
  const l = UI_LABELS[lang] || UI_LABELS.es;
  const ROWS = useMemo(() => buildRows(l, lang), [l, lang]);

  const winnerId = useMemo(() => {
    if (!cards || cards.length < 2) return null;
    const scored = cards.map((c) => ({ id: c.id, score: calculateFinMapleScore(c).total }));
    const max = Math.max(...scored.map((s) => s.score));
    const winners = scored.filter((s) => s.score === max);
    return winners.length === 1 ? winners[0].id : null;
  }, [cards]);

  if (!cards || cards.length < 2) return null;

  const colCount = cards.length + 1;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{ sx: { borderRadius: fullScreen ? 0 : '14px', overflow: 'hidden' } }}
    >
      {/* ── Gradient header ── */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1E3A5F 0%, #2E4F7A 100%)',
          color: '#fff',
          px: { xs: 2, sm: 3 },
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {l.title}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.85 }}>
            {l.subtitle(cards.length)}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          aria-label={l.close}
          sx={{
            color: '#fff',
            bgcolor: 'rgba(255,255,255,0.12)',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.24)' },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 0 }}>
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            maxHeight: '75vh',
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
            scrollSnapType: fullScreen ? 'x mandatory' : 'none',
          }}
        >
          <Table stickyHeader size="small">
            {/* ── Card header ── */}
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    ...stickyLabelSx('#F7F9FC'),
                    zIndex: 3,
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#5F6B7A' }}>
                    {l.feature}
                  </Typography>
                </TableCell>
                {cards.map((card) => {
                  const isWin = card.id === winnerId;
                  return (
                    <TableCell
                      key={card.id}
                      align="center"
                      sx={{
                        bgcolor: isWin ? 'rgba(46,125,50,0.04)' : '#F7F9FC',
                        minWidth: { xs: 140, sm: 180 },
                        scrollSnapAlign: fullScreen ? 'start' : 'none',
                        position: 'relative',
                        pt: isWin ? 0 : 1.5,
                      }}
                    >
                      {isWin && (
                        <Box
                          sx={{
                            height: 4,
                            bgcolor: '#2E7D32',
                            mx: -2,
                            mb: 1.5,
                            borderRadius: '0 0 2px 2px',
                          }}
                        />
                      )}
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 0.5,
                        }}
                      >
                        <Box
                          sx={{
                            borderRadius: '10px',
                            overflow: 'hidden',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            transition: 'transform 0.2s',
                            '&:hover': { transform: 'scale(1.05)' },
                          }}
                        >
                          <CardImage card={card} width={90} height={58} />
                        </Box>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600, lineHeight: 1.2, mt: 0.5 }}
                        >
                          {card.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#5F6B7A' }}>
                          {card.bank}
                        </Typography>
                        {onRemoveCard && (
                          <Button
                            size="small"
                            onClick={() => onRemoveCard(card)}
                            sx={{
                              textTransform: 'none',
                              color: '#5F6B7A',
                              fontSize: '0.75rem',
                              minWidth: 0,
                              '&:hover': { color: '#fff', bgcolor: '#C62828' },
                              transition: 'all 0.2s',
                              borderRadius: '6px',
                              px: 1,
                            }}
                          >
                            {l.remove}
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>

            <TableBody>
              {/* ── FinMaple Score row ── */}
              <TableRow>
                <TableCell sx={stickyLabelSx('#F7F9FC')}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#1A1A1A' }}>
                    FinMaple Score&trade;
                  </Typography>
                </TableCell>
                {cards.map((card) => (
                  <TableCell
                    key={card.id}
                    align="center"
                    sx={{
                      bgcolor: card.id === winnerId ? 'rgba(46,125,50,0.04)' : '#F7F9FC',
                      verticalAlign: 'top',
                    }}
                  >
                    <CompactScore card={card} isWinner={card.id === winnerId} />
                  </TableCell>
                ))}
              </TableRow>

              {/* ── Data rows ── */}
              {ROWS.map((row, idx) => {
                if (row.type === 'section') {
                  return (
                    <TableRow key={`section-${idx}`}>
                      <TableCell
                        colSpan={colCount}
                        sx={{
                          bgcolor: '#F7F9FC',
                          borderBottom: '1px solid #E6E9EE',
                          py: 1,
                          px: 2,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 700,
                            color: '#1E3A5F',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          <span>{row.icon}</span> {row.title}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                }

                const winnerIds = row.bestFn ? row.bestFn(cards) : [];
                return (
                  <TableRow
                    key={row.key}
                    sx={{
                      '&:hover': { bgcolor: 'rgba(30,58,95,0.02)' },
                      transition: 'background-color 0.15s',
                    }}
                  >
                    <TableCell sx={stickyLabelSx('#fff')}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {row.label}
                        </Typography>
                        {row.tooltip && (
                          <Tooltip title={row.tooltip} arrow placement="top">
                            <InfoOutlinedIcon
                              sx={{
                                fontSize: '0.95rem',
                                color: '#E6E9EE',
                                cursor: 'help',
                                '&:hover': { color: '#5F6B7A' },
                                transition: 'color 0.15s',
                              }}
                            />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    {cards.map((card) => {
                      const isRowWinner = winnerIds.includes(card.id) && cards.length > 1;
                      const isOverallWinner = card.id === winnerId;
                      return (
                        <TableCell
                          key={card.id}
                          align="center"
                          sx={{
                            fontSize: '0.875rem',
                            position: 'relative',
                            bgcolor: isRowWinner
                              ? 'rgba(46,125,50,0.04)'
                              : isOverallWinner
                                ? 'rgba(46,125,50,0.02)'
                                : 'inherit',
                            fontWeight: isRowWinner ? 600 : 400,
                          }}
                        >
                          {isRowWinner && (
                            <Box
                              sx={{
                                position: 'absolute',
                                left: 0,
                                top: 0,
                                bottom: 0,
                                width: 3,
                                bgcolor: '#2E7D32',
                                borderRadius: '0 2px 2px 0',
                              }}
                            />
                          )}
                          {row.render(card)}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}

              {/* ── CTA row ── */}
              <TableRow>
                <TableCell sx={{ ...stickyLabelSx('#fff'), borderTop: '2px solid #E6E9EE' }} />
                {cards.map((card) => (
                  <TableCell
                    key={card.id}
                    align="center"
                    sx={{ py: 2.5, borderTop: '2px solid #E6E9EE' }}
                  >
                    <Button
                      variant="contained"
                      href={card.affiliate_link || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      endIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
                      sx={{
                        bgcolor: '#1E3A5F',
                        '&:hover': {
                          bgcolor: '#1A3152',
                          transform: 'translateY(-1px)',
                          boxShadow: '0 4px 12px rgba(30,58,95,0.3)',
                        },
                        textTransform: 'none',
                        fontWeight: 700,
                        borderRadius: '10px',
                        px: 3,
                        py: 1,
                        minHeight: 44,
                        transition: 'all 0.2s',
                      }}
                    >
                      {l.apply}
                    </Button>
                  </TableCell>
                ))}
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
    </Dialog>
  );
};

export default ComparisonModal;
